import { SvgIcon, Text } from '@cinch-labs/design-system'
import { useViewport } from '@cinch-labs/shared-util'
import { StoryblokImage, Text as StoryblokText } from '@cinch-nx/data-storyblok'
import { TrackingEventTypes, useAnalytics } from '@cinch-nx/shared-analytics'
import Image from 'next/legacy/image'

import TitleWithLogo from '../title-with-logo/title-with-logo'
import styles from './download-app.module.css'

export interface DownloadAppProps {
  headingLeftText?: string
  headingRightText?: string
  headingLogo?: 'cinchShield' | 'cinchCharge' | 'cinchChargePurple'
  image: StoryblokImage
  items: StoryblokText[]
}

const DownloadApp: React.FC<DownloadAppProps> = ({
  headingLeftText,
  headingRightText,
  headingLogo,
  image,
  items,
}) => {
  const { vw } = useViewport({
    updateOnResize: true,
    updateOnOrientationChange: true,
  })
  const isMobile = vw < 1024

  const { trackEvent } = useAnalytics()

  const trackingEventCategoryString = `${headingLeftText} ${
    headingLogo === 'cinchChargePurple' ? 'cinchCharge' : headingLogo
  } ${headingRightText}`

  return (
    <div className={styles.container}>
      <div className={styles.contentWrapper}>
        <TitleWithLogo
          headingLevel={'h3'}
          leftText={headingLeftText}
          rightText={headingRightText}
          textClassName={styles.title}
          logo={isMobile ? undefined : headingLogo}
          logoHeight={'32'}
          logoWidth={'162'}
          centered={isMobile ? true : false}
          wrapperClassName={styles.titleWrapper}
        />

        <div className={styles.itemsWrapper}>
          {items.map((item, index) => (
            <div key={'tick-item-' + index} className={styles.tickItem}>
              <div className={styles.svgWrapper}>
                <SvgIcon name="tick" size={14} color="deepPurple" />
              </div>
              <Text className={styles.itemText}>{item.text}</Text>
            </div>
          ))}
        </div>

        <div className={styles.downloadApp}>
          <a
            href="https://apps.apple.com/gb/app/id1640016085"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent({
                eventName: `cinchCharge landing page - App Store click`,
                data: {
                  event: {
                    name: 'click',
                    category:
                      trackingEventCategoryString || 'Download cinchcharge app',
                    action: 'app download cta',
                    label: 'App Store',
                    value: 'https://apps.apple.com/gb/app/id1640016085',
                  },
                },
                type: TrackingEventTypes.ADOBE,
              })
            }}
          >
            <img
              src={
                'https://a.storyblok.com/f/132543/284x84/142c238d5c/app-store-2x.png'
              }
              height="42px"
              width="142px"
              alt="app store"
            />
          </a>

          <a
            href="https://play.google.com/store/apps/details?id=com.pauatech.cinchCharge"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent({
                eventName: `cinchCharge landing page - Google Play click`,
                data: {
                  event: {
                    name: 'click',
                    category:
                      trackingEventCategoryString || 'Download cinchcharge app',
                    action: 'app download cta',
                    label: 'Google Play',
                    value:
                      'https://play.google.com/store/apps/details?id=com.pauatech.cinchCharge',
                  },
                },
                type: TrackingEventTypes.ADOBE,
              })
            }}
          >
            <img
              src={
                'https://a.storyblok.com/f/132543/284x84/d1228f4837/google-play-2x.png'
              }
              height="42px"
              width="142px"
              alt="play store"
            />
          </a>
        </div>
      </div>

      {!isMobile && (
        <div className={styles.imageWrapper}>
          <Image
            src={image.filename}
            alt={image.alt ?? undefined}
            objectFit="contain"
            layout="fill"
            data-testid="mobile-app-image"
          />
        </div>
      )}
    </div>
  )
}

export default DownloadApp
